export function validateCPF(cpf: string): boolean {
  const cleanedCPF = cpf.replace(/\D/g, "");

  if (cleanedCPF.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(cleanedCPF)) return false;

  let sum = 0;
  for (let i = 1; i <= 9; i++) {
    sum += parseInt(cleanedCPF.charAt(i - 1), 10) * (11 - i);
  }
  let remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleanedCPF.charAt(9), 10)) return false;

  sum = 0;
  for (let i = 1; i <= 10; i++) {
    sum += parseInt(cleanedCPF.charAt(i - 1), 10) * (12 - i);
  }
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleanedCPF.charAt(10), 10)) return false;

  return true;
}

// CNPJ alfanumérico (Receita Federal, a partir de 31/07/2026): mantém 14
// caracteres, sendo os 12 primeiros alfanuméricos (raiz + ordem do
// estabelecimento) e os 2 últimos sempre numéricos (dígitos verificadores).
// CNPJs numéricos antigos continuam válidos, pois são um subconjunto deste
// formato e o cálculo do DV abaixo produz o mesmo resultado de sempre.
const CNPJ_FORMAT = /^[A-Z0-9]{12}\d{2}$/;

// Valor de cálculo de cada caractere: dígitos 0-9 mantêm seu valor; letras
// A-Z viram 17-42 (código ASCII da letra maiúscula menos 48), conforme regra
// oficial da Receita Federal para o dígito verificador do CNPJ alfanumérico.
function cnpjCharValue(char: string): number {
  return char.charCodeAt(0) - 48;
}

// Módulo 11 com pesos de 2 a 9 distribuídos da direita para a esquerda
// (reiniciando em 2 após o peso 9), igual ao cálculo já usado para CNPJ
// numérico, só que agora considerando também caracteres alfanuméricos.
function calcCnpjDigit(base: string): number {
  let sum = 0;
  let weight = 2;
  for (let i = base.length - 1; i >= 0; i--) {
    sum += cnpjCharValue(base.charAt(i)) * weight;
    weight = weight === 9 ? 2 : weight + 1;
  }
  const remainder = sum % 11;
  return remainder < 2 ? 0 : 11 - remainder;
}

export function validateCNPJ(cnpj: string): boolean {
  const cleanedCNPJ = cnpj.replace(/[.\-/\s]/g, "").toUpperCase();

  if (!CNPJ_FORMAT.test(cleanedCNPJ)) return false;
  if (/^(.)\1{13}$/.test(cleanedCNPJ)) return false;

  const base12 = cleanedCNPJ.substring(0, 12);
  const digit1 = calcCnpjDigit(base12);
  if (digit1 !== parseInt(cleanedCNPJ.charAt(12), 10)) return false;

  const digit2 = calcCnpjDigit(base12 + digit1.toString());
  if (digit2 !== parseInt(cleanedCNPJ.charAt(13), 10)) return false;

  return true;
}

export function validateOrganizationDocument(
  value: string,
  personType: "PF" | "PJ",
): string | true {
  if (personType === "PF") {
    const digits = value.replace(/\D/g, "");
    if (digits.length !== 11) {
      return "CPF deve conter exatamente 11 dígitos.";
    }
    if (!validateCPF(value)) {
      return "CPF inválido.";
    }
    return true;
  }

  const cleaned = value.replace(/[.\-/\s]/g, "");
  if (cleaned.length !== 14) {
    return "CNPJ deve conter 14 caracteres (12 alfanuméricos + 2 dígitos verificadores).";
  }
  if (!validateCNPJ(value)) {
    return "CNPJ inválido.";
  }
  return true;
}

function applyDocumentMask(cleaned: string, personType: "PF" | "PJ"): string {
  if (personType === "PF") {
    const digits = cleaned.slice(0, 11);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
    if (digits.length <= 9) {
      return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
    }
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(
      6,
      9,
    )}-${digits.slice(9)}`;
  }

  const chars = cleaned.slice(0, 14);
  if (chars.length <= 2) return chars;
  if (chars.length <= 5) return `${chars.slice(0, 2)}.${chars.slice(2)}`;
  if (chars.length <= 8) {
    return `${chars.slice(0, 2)}.${chars.slice(2, 5)}.${chars.slice(5)}`;
  }
  if (chars.length <= 12) {
    return `${chars.slice(0, 2)}.${chars.slice(2, 5)}.${chars.slice(
      5,
      8,
    )}/${chars.slice(8)}`;
  }
  return `${chars.slice(0, 2)}.${chars.slice(2, 5)}.${chars.slice(
    5,
    8,
  )}/${chars.slice(8, 12)}-${chars.slice(12)}`;
}

function cleanDocumentInput(value: string, personType: "PF" | "PJ"): string {
  if (personType === "PF") {
    return value.replace(/\D/g, "").slice(0, 11);
  }

  const cleaned = value
    .replace(/[.\-/\s]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
  const base = cleaned.slice(0, 12);
  const checkDigits = cleaned.slice(12).replace(/\D/g, "").slice(0, 2);
  return (base + checkDigits).slice(0, 14);
}

export function maskOrganizationDocument(
  value: string,
  personType: "PF" | "PJ" = "PJ",
  previousValue = "",
): string {
  const isDeletion =
    Boolean(previousValue) && value.length < previousValue.length;
  let cleaned = cleanDocumentInput(value, personType);

  if (isDeletion) {
    const previousCleaned = cleanDocumentInput(previousValue, personType);
    if (cleaned.length === previousCleaned.length && cleaned.length > 0) {
      cleaned = cleaned.slice(0, -1);
    }
  }

  return applyDocumentMask(cleaned, personType);
}

export function formatOrganizationDocument(
  value: string,
  personType?: "PF" | "PJ",
): string {
  const cleaned = value.replace(/[.\-/\s]/g, "").toUpperCase();
  const type = personType ?? (cleaned.length === 11 ? "PF" : "PJ");
  return maskOrganizationDocument(value, type);
}

export const documentMaxLength = (personType: "PF" | "PJ") =>
  personType === "PF" ? 14 : 18;
